import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';

// Admin-configured supplemental questions for the storefront customer
// registration form (PD-13659 / PD-14329). A deliberate SIBLING to
// custom_field_definition: it shares the field SHAPE (type / label / options /
// required) so the same renderer + validation can be reused, but it is a
// separate concept — these are TRANSIENT intake questions whose answers are
// captured on `client_registration_new` (the submission record + summary
// email), NOT persisted as columns on the client entity. A question is only
// promoted to a real custom field if its answer ever needs to live on the
// client / be filterable.
//
// Scope: `fk_portal_site__id` NULL = the tenant-wide DEFAULT set (applies to
// every storefront); a non-null portal id is a per-storefront OVERRIDE.
// Resolution is "use the portal's own rows if any exist, else fall back to the
// tenant default" (handled in the resolver service in PD-14330). Uniqueness of
// `fieldKey` within a scope is enforced in the service layer, NOT by a DB unique
// index, because MySQL treats NULL `fk_portal_site__id` values as distinct — a
// unique index could not guard duplicate tenant-default keys.
@Index(
	'idx_registration_field_portal_active',
	['fkPortalSiteId', 'isActive'],
	{},
)
@Entity('registration_field')
export class RegistrationFieldEntity extends BaseEntity {
	// Null = tenant-wide default set; non-null = override for one portal_site.
	@Column('int', { name: 'fk_portal_site__id', nullable: true, unsigned: true })
	public fkPortalSiteId!: number | null;

	// Stable key the submitted answer is stored under in
	// client_registration_new.registrationFields.
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	@Column('varchar', { name: 'label', length: 255 })
	public label!: string;

	// Mirrors CustomFieldDefinition.fieldType (text | textarea | number | select
	// | multiselect | boolean | date | datetime | phone | email | url) so the
	// storefront can reuse the shared CustomFieldEditor renderer unchanged.
	@Column('varchar', { name: 'fieldType', length: 50 })
	public fieldType!: string;

	@Column('varchar', { name: 'placeholder', nullable: true, length: 255 })
	public placeholder!: string | null;

	@Column('varchar', { name: 'helpText', nullable: true, length: 500 })
	public helpText!: string | null;

	@Column('tinyint', { name: 'isRequired', width: 1, default: () => "'0'" })
	public isRequired!: boolean;

	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	// Whether this field's answer is included in the lead-registration summary
	// email + the client-detail Recent Submissions card.
	@Column('tinyint', {
		name: 'includeInEmail',
		width: 1,
		default: () => "'1'",
	})
	public includeInEmail!: boolean;

	@Column('text', { name: 'defaultValue', nullable: true })
	public defaultValue!: string | null;

	@Column('json', {
		name: 'validationRules',
		nullable: true,
		comment: 'JSON object with validation rules (min, max, pattern, etc.)',
	})
	public validationRules!: Record<string, unknown> | null;

	@Column('json', {
		name: 'selectOptions',
		nullable: true,
		comment: 'Array of options for select/multiselect field types',
	})
	public selectOptions!: Array<{ value: string; label: string }> | null;

	// Logical relation only (no DB-level FK constraint) so the additive
	// schema-sync just creates the column + index without managing a constraint.
	// Null for tenant-default rows.
	@ManyToOne(() => PortalSiteEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public fkPortalSite!: PortalSiteEntity | null;
}
